/**
 * React hooks for Workflow Chaining data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CACHE_TIME } from '@/config/queryDefaults';
import { workflowSubscriptionApi, eventCatalogApi, workflowDefinitionsApi, commandCatalogApi } from '../api';
import type {
  CreateUpdateWorkflowSubscription,
  WorkflowSubscriptionListInput,
} from '../types';

// Query keys
export const QUERY_KEYS = {
  subscriptions: ['workflow-subscriptions'] as const,
  subscription: (id: string) => ['workflow-subscription', id] as const,
  chainOverview: ['workflow-chain-overview'] as const,
  events: ['event-catalog'] as const,
  eventDetail: (type: string) => ['event-detail', type] as const,
  workflows: ['workflow-definitions'] as const,
  workflowDetail: (id: string) => ['workflow-definition', id] as const,
  commands: ['command-catalog'] as const,
};

/**
 * Hook to fetch list of workflow subscriptions
 */
export function useWorkflowSubscriptions(input: WorkflowSubscriptionListInput) {
  return useQuery({
    queryKey: [...QUERY_KEYS.subscriptions, input],
    queryFn: () => workflowSubscriptionApi.getList(input),
  });
}

/**
 * Hook to fetch a single workflow subscription
 */
export function useWorkflowSubscription(id: string | undefined) {
  return useQuery({
    queryKey: QUERY_KEYS.subscription(id!),
    queryFn: () => workflowSubscriptionApi.get(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a workflow subscription
 */
export function useCreateWorkflowSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateUpdateWorkflowSubscription) => 
      workflowSubscriptionApi.create(input),
    onSuccess: () => {
      // Invalidate subscriptions list to trigger refetch
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptions });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chainOverview });
    },
  });
}

/**
 * Hook to update a workflow subscription
 */
export function useUpdateWorkflowSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateUpdateWorkflowSubscription }) =>
      workflowSubscriptionApi.update(id, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription(variables.id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptions });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chainOverview });
    },
  });
}

/**
 * Hook to delete a workflow subscription
 */
export function useDeleteWorkflowSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => workflowSubscriptionApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptions });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chainOverview });
    },
  });
}

/**
 * Hook to fetch workflow chaining overview
 */
export function useChainOverview() {
  return useQuery({
    queryKey: QUERY_KEYS.chainOverview,
    queryFn: () => workflowSubscriptionApi.getChainOverview(),
  });
}

/**
 * Hook to validate a subscription
 */
export function useValidateSubscription() {
  return useMutation({
    mutationFn: (input: CreateUpdateWorkflowSubscription) =>
      workflowSubscriptionApi.validate(input),
  });
}

/**
 * Hook to simulate chain for an event
 */
export function useSimulateChain(eventType: string | undefined) {
  return useQuery({
    queryKey: ['simulate-chain', eventType],
    queryFn: () => workflowSubscriptionApi.simulateChain(eventType!),
    enabled: !!eventType,
  });
}

/**
 * Hook to fetch event catalog
 */
export function useEventCatalog() {
  return useQuery({
    queryKey: QUERY_KEYS.events,
    queryFn: () => eventCatalogApi.getEvents(),
  });
}

/**
 * Hook to fetch event details
 */
export function useEventDetail(eventType: string | undefined) {
  return useQuery({
    queryKey: QUERY_KEYS.eventDetail(eventType!),
    queryFn: () => eventCatalogApi.getEventDetails(eventType!),
    enabled: !!eventType,
  });
}

/**
 * Hook to fetch workflow definitions
 */
export function useWorkflowDefinitions() {
  return useQuery({
    queryKey: QUERY_KEYS.workflows,
    queryFn: () => workflowDefinitionsApi.getWorkflows(),
    staleTime: CACHE_TIME.METADATA,
  });
}

/**
 * Hook to fetch workflow definition details
 */
export function useWorkflowDefinitionDetail(definitionId: string | undefined) {
  return useQuery({
    queryKey: QUERY_KEYS.workflowDetail(definitionId!),
    queryFn: () => workflowDefinitionsApi.getWorkflowDetails(definitionId!),
    enabled: !!definitionId,
  });
}

/**
 * Hook to fetch command catalog
 */
export function useCommandCatalog() {
  return useQuery({
    queryKey: QUERY_KEYS.commands,
    queryFn: () => commandCatalogApi.getCommands(),
  });
}

